import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";

import Cliente from "./Cliente";
import AdicionarLicenca from "./AdicionarLicenca";
import ListarLicencas from "./ListarLicencas";
import ListarPagamentos from "./ListarPagamentos";
import ResetSenhaUser from "./ResetSenhaUser";
import Suporte from "./Suporte";
import Downloads from "./Downloads";

import { useAuth } from "../auth";
import api from "../api";
import "./estilodashuser.css";

export default function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, token, logout } = useAuth();

  const [nomeCliente, setNomeCliente] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [carregandoNome, setCarregandoNome] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !token || !user) {
      navigate("/login", { replace: true });
      return;
    }
    fetchClienteNome(user.id);
  }, [isAuthenticated, token, user, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const fetchClienteNome = async (userId) => {
    try {
      setCarregandoNome(true);
      const response = await api.get(`/api/clientes/${userId}`);
      setNomeCliente(response.data?.nome || "Cliente");
    } catch (error) {
      console.error("Erro ao buscar nome do cliente:", error);
      setNomeCliente("Cliente");
    } finally {
      setCarregandoNome(false);
    }
  };

  const handleLogout = () => {
    try {
      if (typeof logout === "function") logout();
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch (e) {
      console.error("Erro ao fazer logout:", e);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const menuItems = useMemo(
    () => [
      {
        path: "/dashboard-cliente/cliente",
        icon: "👤",
        label: "Minha Conta",
        help: "Confira seus dados, veja suas assinaturas e solicite alteração de plano.",
      },
      {
        path: "/dashboard-cliente/adicionar-licenca",
        icon: "➕",
        label: "Adicionar Licença",
        help: "Cadastre uma nova conta MT5 dentro dos slots disponíveis na sua contratação.",
      },
      {
        path: "/dashboard-cliente/listar-licencas",
        icon: "📜",
        label: "Minhas Licenças",
        help: "Veja quais robôs e contas MT5 já estão cadastrados na sua área.",
      },
      {
        path: "/dashboard-cliente/listar-pagamentos",
        icon: "💳",
        label: "Meus Pagamentos",
        help: "Consulte valores, status, descrição e meses de referência das cobranças.",
      },
      {
        path: "/dashboard-cliente/downloads",
        icon: "📥",
        label: "Downloads",
        help: "Baixe robôs, templates, manuais e arquivos de apoio.",
      },
      {
        path: "/dashboard-cliente/suporte",
        icon: "📱",
        label: "Suporte",
        help: "Entre em contato com o time e encontre orientações rápidas.",
      },
      {
        path: "/dashboard-cliente/reset-senha",
        icon: "🔒",
        label: "Alterar Senha",
        help: "Mantenha sua conta segura alterando sua senha de acesso.",
      },
    ],
    []
  );

  const currentMenuItem = useMemo(() => {
    return menuItems.find((item) => location.pathname === item.path) || null;
  }, [menuItems, location.pathname]);

  const isActive = (path) => location.pathname === path;

  const goTo = (path) => {
    navigate(path);
  };

  return (
    <div className="dash-shell">
      {sidebarOpen && (
        <div
          className="dash-overlay"
          onClick={() => setSidebarOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu"
        />
      )}

      <aside className={`dash-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="dash-brand">
          <div className="dash-brand-title">Quantum Trades</div>
          <div className="dash-brand-subtitle">Área do Cliente</div>
        </div>

        <nav className="dash-menu">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`dash-menu-item ${isActive(item.path) ? "active" : ""}`}
              onClick={() => goTo(item.path)}
              type="button"
              title={item.help}
            >
              <span className="dash-menu-ico">{item.icon}</span>
              <span className="dash-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="dash-sidebar-footer">
          <button className="dash-logout" onClick={handleLogout} type="button">
            <span className="dash-menu-ico">🚪</span>
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <main className="dash-main">
        <header className="dash-topbar">
          <button
            className="dash-burger"
            onClick={() => setSidebarOpen((v) => !v)}
            type="button"
            aria-label="Abrir menu"
          >
            ☰
          </button>

          <div className="dash-topbar-title">
            <div className="dash-welcome">
              {carregandoNome
                ? "Carregando sua área..."
                : `Bem-vindo, ${nomeCliente || "Cliente"}!`}
            </div>
            <div className="dash-welcome-sub">
              {currentMenuItem?.help ||
                "Gerencie sua contratação, licenças, pagamentos e downloads em um só lugar."}
            </div>
          </div>
        </header>

        <section className="dash-content">
          <div className="user-orientation-card">
            <div className="user-orientation-title">Orientação rápida</div>
            <div className="user-orientation-text">
              Use o menu lateral para navegar entre suas informações. Primeiro confira sua contratação em <strong>Minha Conta</strong>,
              depois cadastre ou revise suas contas em <strong>Adicionar Licença</strong> e <strong>Minhas Licenças</strong>.
              Sempre que precisar ajustar plano, dados ou cobrança, consulte a área correspondente ou fale com o suporte.
            </div>
          </div>

          <Routes>
            <Route path="/" element={<Navigate to="cliente" replace />} />
            <Route path="cliente" element={<Cliente userId={user?.id} />} />
            <Route path="adicionar-licenca" element={<AdicionarLicenca userId={user?.id} />} />
            <Route path="listar-licencas" element={<ListarLicencas userId={user?.id} />} />
            <Route path="listar-pagamentos" element={<ListarPagamentos userId={user?.id} />} />
            <Route path="downloads" element={<Downloads userId={user?.id} />} />
            <Route path="suporte" element={<Suporte userId={user?.id} />} />
            <Route path="reset-senha" element={<ResetSenhaUser userId={user?.id} />} />
            <Route path="*" element={<Navigate to="cliente" replace />} />
          </Routes>
        </section>
      </main>
    </div>
  );
}
